/**
 * Phase 7b — Section Notes de crédit d'une facture.
 *
 * - Affiche les NC associées + summary (credité, restant à payer).
 * - Bouton "Émettre une note de crédit" ouvre modal (montant ≤ reste créditable, motif obligatoire).
 * - Bouton PDF par NC.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ViewPdfLink } from "@/components/ViewPdfLink";
import { Download, FilePlus2, Loader2, Ban } from "lucide-react";
import { formatCurrencyBE } from "@/lib/currency";
import { useAuth } from "@/contexts/AuthContext";
import { hasRole } from "@/lib/roles";

export default function CreditNotesBlock({ invoice, onCredited }) {
  const { state } = useAuth();
  const isGestionnaireChantier = hasRole(state?.user, ["gestionnaire_chantier"]);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/invoices/${invoice.id}/credit-summary`);
      setSummary(data);
    } catch { /* invoice may not exist yet */ }
  };
  useEffect(() => { load(); }, [invoice.id]);

  const remaining = summary?.remaining_tvac ?? invoice.amount_tvac;
  // Phase B rôle gestionnaire_chantier (2026-09-10) — Émission NC INTERDITE
  // (matrice). Backend renvoie déjà 403, on masque aussi le bouton côté UI.
  const canCredit = !isGestionnaireChantier && remaining > 0.01 && invoice.status !== "paid";

  const submit = async () => {
    const n = parseFloat((amount || "").replace(",", "."));
    if (isNaN(n) || n <= 0) {
      toast.error("Montant invalide"); return;
    }
    if (n > remaining + 0.01) {
      toast.error(`Sur-crédit interdit — max ${formatCurrencyBE(remaining)}`); return;
    }
    if ((reason || "").trim().length < 5) {
      toast.error("Motif obligatoire (5 caractères min)"); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${invoice.id}/credit-notes`, {
        amount_tvac: n, reason: reason.trim(),
      });
      toast.success(`Note de crédit ${data.number} émise`, {
        description: data.kind === "total" ? "Facture totalement créditée · relances annulées" : "Facture partiellement créditée",
      });
      setOpen(false); setAmount(""); setReason("");
      await load();
      onCredited?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const downloadCnPdf = null; // Lot 2026-10-18 20h55 — remplacé par <ViewPdfLink> inline ci-dessous

  if (!summary) return null;

  return (
    <div className="mt-3 rounded border border-slate-800 bg-slate-950/40 p-3" data-testid={`credit-notes-block-${invoice.id}`}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <Ban className="h-3.5 w-3.5 text-[#F0C572]" />
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-300">
            Notes de crédit
          </span>
          {summary.credited && (
            <span className="rounded bg-[#F0C572]/20 px-1.5 py-0.5 text-[10px] uppercase text-[#F0C572]"
                  data-testid={`credit-badge-${invoice.id}`}>
              {summary.fully_credited ? "Créditée totalement" : "Créditée partiellement"}
            </span>
          )}
        </div>
        {canCredit && (
          <Button
            size="sm"
            onClick={() => setOpen(true)}
            className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
            data-testid={`credit-note-open-btn-${invoice.id}`}
          >
            <FilePlus2 className="mr-1 h-3 w-3" /> Émettre une NC
          </Button>
        )}
      </div>

      {summary.credited && (
        <div className="mb-2 grid grid-cols-2 gap-2 text-xs">
          <div className="rounded bg-slate-900/60 p-2">
            <div className="text-slate-500">Total crédité</div>
            <div className="tabular font-semibold text-[#F0C572]" data-testid={`credited-amount-${invoice.id}`}>
              {formatCurrencyBE(summary.credited_amount_tvac)}
            </div>
          </div>
          <div className="rounded bg-slate-900/60 p-2">
            <div className="text-slate-500">Reste à payer</div>
            <div className="tabular font-semibold text-slate-100">
              {formatCurrencyBE(summary.remaining_tvac)}
            </div>
          </div>
        </div>
      )}

      {summary.credit_notes.length === 0 ? (
        <div className="text-xs italic text-slate-500">Aucune note de crédit émise.</div>
      ) : (
        <ul className="space-y-1">
          {summary.credit_notes.map((cn) => (
            <li key={cn.id} className="flex items-center justify-between gap-2 text-xs"
                data-testid={`credit-note-item-${cn.id}`}>
              <div className="flex items-center gap-1.5">
                <span className="font-mono font-semibold text-slate-200">{cn.number}</span>
                <span className="rounded px-1.5 py-0.5 text-[10px] uppercase"
                      style={{ background: cn.kind === "total" ? "#DC262633" : "#F0C57233", color: cn.kind === "total" ? "#EF4444" : "#F0C572" }}>
                  {cn.kind}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="tabular text-slate-300">{formatCurrencyBE(cn.amount_tvac)}</span>
                <ViewPdfLink
                  entityType="credit_note"
                  entityId={cn.id}
                  enabled={true}
                  label=""
                  icon={<Download className="h-3 w-3" />}
                  testid={`credit-note-pdf-btn-${cn.id}`}
                  as="button-outline"
                  className="h-7 px-2 border-slate-700 bg-transparent text-slate-400 hover:text-slate-100"
                />
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Modal émission NC */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-slate-800 bg-slate-900" data-testid={`credit-note-modal-${invoice.id}`}>
          <DialogHeader>
            <DialogTitle>Émettre une note de crédit</DialogTitle>
            <DialogDescription className="text-slate-400">
              Sur la facture <b>{invoice.number}</b> ({formatCurrencyBE(invoice.amount_tvac)} TVAC).
              Reste créditable : <b>{formatCurrencyBE(remaining)}</b>.
              {" "}Une NC ne peut pas être supprimée après émission.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="cn-amount">Montant TVAC à créditer *</Label>
              <Input
                id="cn-amount" value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={`≤ ${formatCurrencyBE(remaining)}`}
                inputMode="decimal"
                className="border-slate-700 bg-slate-950/40 text-slate-100"
                data-testid="credit-note-amount-input"
              />
            </div>
            <div>
              <Label htmlFor="cn-reason">Motif *</Label>
              <Textarea
                id="cn-reason" value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ex: erreur de facturation ligne 3, annulation partielle chantier…"
                rows={3}
                className="border-slate-700 bg-slate-950/40 text-slate-100"
                data-testid="credit-note-reason-input"
              />
              <p className="mt-1 text-xs text-slate-500">Visible sur le PDF de la NC et pour le client.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-700 bg-slate-800/60">
              Annuler
            </Button>
            <Button onClick={submit} disabled={busy}
                    className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
                    data-testid="credit-note-submit-btn">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Émettre la NC
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
